import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
import { PrismicRichText } from "@prismicio/react";
import { GatsbyImage } from "gatsby-plugin-image";
import { FormatDate } from "../../components/utils/format-date";

const MobileArchiveContainer = styled.div`
  padding: 120px 0 20px 0;
`;

const YearGroup = styled.div`
  display: flex;
  padding: 0 20px;
`;

const YearIndicator = styled.div`
  width: 100px;
  position: sticky;
  top: 120px;
  height: fit-content;

  & p {
    margin: 0;
  }
`;

const ExhibitionsContainer = styled.div`
  flex: 1;
`;

const ExhibitionItem = styled.div`
  margin-bottom: 40px;

  .gatsby-image-wrapper {
    width: 100%;
    margin-bottom: 20px;

    img {
      object-fit: contain !important;
    }
  }

  .title-container {
    h1 {
      margin: 0;
    }

    p {
      margin: 0;
    }
  }
`;

const ScrollToTop = styled.button`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 20px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: pointer;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 42px;

  &:focus {
    outline: none;
  }
`;

const MobileArchive = ({ items }) => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Group exhibitions by year
  const groupedExhibitions = items.reduce((acc, item) => {
    const year = new Date(item.data.start_date).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(item);
    return acc;
  }, {});

  const years = Object.keys(groupedExhibitions).sort((a, b) => b - a);

  return (
    <MobileArchiveContainer>
      {years.map((year) => (
        <YearGroup key={year}>
          <YearIndicator>
            <p>{year}</p>
          </YearIndicator>
          <ExhibitionsContainer>
            {groupedExhibitions[year].map((item, index) => (
              <ExhibitionItem key={`mobile_exhibition_${index}_${item.id}`}>
                <Link to={item.url}>
                  <GatsbyImage
                    image={item.data.desktop_thumbnail.gatsbyImageData}
                    alt={item.data.desktop_thumbnail.alt}
                  />

                  <div className="title-container">
                    <h1>
                      <PrismicRichText
                        field={item.data.artist.richText}
                        components={{
                          heading2: ({ children }) => <span>{children}, </span>,
                        }}
                      />

                      <PrismicRichText
                        field={item.data.title.richText}
                        components={{
                          heading1: ({ children }) => (
                            <span className="italic">{children}</span>
                          ),
                        }}
                      />
                    </h1>

                    <PrismicRichText
                      field={item.data.additional_info.richText}
                    />

                    <p>
                      {FormatDate(item.data.start_date, item.data.end_date)}
                    </p>
                  </div>
                </Link>
              </ExhibitionItem>
            ))}
          </ExhibitionsContainer>
        </YearGroup>
      ))}

      <ScrollToTop
        onClick={scrollToTop}
        $visible={showScrollTop}
        aria-label="Scroll to top"
      >
        ↑
      </ScrollToTop>
    </MobileArchiveContainer>
  );
};

export default MobileArchive;
